import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect } from 'react';
import './styles/global.css';

import Cursor from './components/Cursor';
import AmbientBackground from './components/AmbientBackground';
import ScrollProgress from './components/ScrollProgress';
import FloatingNav from './components/FloatingNav';
import Hero from './components/Hero';
import Skills from './components/Skills';
import Projects from './components/Projects';
import About from './components/About';
import Experience from './components/Experience';
import Currently from './components/Currently';
import Contact from './components/Contact';
import ProjectDetail from './pages/ProjectDetail';

// Handles scrollTo state passed from ProjectDetail back button or FloatingNav
function ScrollHandler() {
  const location = useLocation();

  useEffect(() => {
    const targetId = location.state?.scrollTo;
    if (targetId) {
      let attempts = 0;
      const maxAttempts = 50; // 50 * 30ms = 1500ms max wait for exit animations and page mount

      const interval = setInterval(() => {
        attempts++;
        const el = document.getElementById(targetId);
        if (el) {
          clearInterval(interval);
          setTimeout(() => {
            if (targetId === 'hero') {
              window.scrollTo({ top: 0, behavior: 'smooth' });
            } else {
              el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }, 35);
          window.history.replaceState({}, '');
        } else if (attempts >= maxAttempts) {
          clearInterval(interval);
          window.history.replaceState({}, '');
        }
      }, 30);

      return () => clearInterval(interval);
    }
  }, [location.pathname, location.state]);

  return null;
}

function HomePage() {
  return (
    <>
      <Hero />
      <Skills />
      <Projects />
      <About />
      <Experience />
      <Currently />
      <Contact />
    </>
  );
}

const pageVariants = {
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.45, ease: [0.16, 1, 0.3, 1] } },
  exit:    { opacity: 0, y: -12, transition: { duration: 0.25, ease: [0.65, 0, 0.35, 1] } },
};

function AnimatedRoutes() {
  const location = useLocation();
  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={location.pathname}
        variants={pageVariants}
        initial="initial"
        animate="animate"
        exit="exit"
      >
        <Routes location={location}>
          <Route path="/"               element={<HomePage />} />
          <Route path="/projects/:id"   element={<ProjectDetail />} />
        </Routes>
      </motion.div>
    </AnimatePresence>
  );
}

function FoilTracker() {
  useEffect(() => {
    let rafId = null;
    const onMouseMove = (e) => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        const x = e.clientX / window.innerWidth - 0.5;
        const y = e.clientY / window.innerHeight - 0.5;
        const angle = Math.atan2(y, x) * (180 / Math.PI) + 90;
        document.documentElement.style.setProperty('--foil-angle', `${angle.toFixed(1)}deg`);
      });
    };
    window.addEventListener('mousemove', onMouseMove, { passive: true });
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);
  return null;
}

export default function App() {
  return (
    <BrowserRouter>
      <ScrollHandler />
      <FoilTracker />
      <AmbientBackground />
      <Cursor />
      <ScrollProgress />
      <FloatingNav />
      <AnimatedRoutes />
    </BrowserRouter>
  );
}
